import { TaskDeleteAction } from "./TaskDeleteAction";
import { taskPeriodLabel } from "./taskPeriod";
import { postponeDeadline } from "./requireTaskSchedule";
import { useQueryClient } from "@tanstack/react-query";
import { MoreVertical } from "lucide-react";

import {
  useGetIdentity,
  useGetRecordRepresentation,
  useTranslate,
  useUpdate,
} from "ra-core";

import { useEffect, useState } from "react";

import { ReferenceField } from "@/components/admin/reference-field";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useConfigurationContext } from "../root/ConfigurationContext";

import type { Contact, Sale, Task as TData } from "../types";

import { TaskEdit } from "./TaskEdit";
import { TaskEditSheet } from "./TaskEditSheet";

import { useIsMobile } from "@/hooks/use-mobile";

import { TaskQuickStatus } from "./TaskQuickStatus";
import { TaskWorkDialog } from "./TaskWorkDialog";

export const Task = ({
  task,
  showContact,
  showAssignee,
  linkedContacts = [],
}: {
  task: TData;
  showContact?: boolean;
  showAssignee?: boolean;
  linkedContacts?: Contact[];
}) => {
  const isMobile = useIsMobile();

  const { identity } = useGetIdentity();

  const [openWork, setOpenWork] = useState(false);

  const canEditTask =
    !!identity &&
    (identity.administrator === true ||
      task.created_by_sales_id === identity.id);

  const { taskTypes } = useConfigurationContext();

  const translate = useTranslate();

  const queryClient = useQueryClient();

  const getContactRepresentation = useGetRecordRepresentation("contacts");

  const getSalesRepresentation = useGetRecordRepresentation("sales");

  const [openEdit, setOpenEdit] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  /*
   * Контакты теперь загружаются
   * один раз на весь видимый список
   * в TasksIterator и передаются сюда.
   *
   * Это убирает отдельные запросы
   * task_contacts + contacts
   * для каждой строки задачи.
   */
  const firstContact = linkedContacts[0];

  const additionalContactsCount = Math.max(linkedContacts.length - 1, 0);

  const handleCloseEdit = () => {
    setOpenEdit(false);
  };

  const [update, { isPending: isUpdatePending, isSuccess, variables }] =
    useUpdate();

  const handleEdit = () => {
    setOpenEdit(true);
  };

  const handleCheck = () => () => {
    const completed = !task.done_date;

    update("tasks", {
      id: task.id,

      data: {
        done_date: completed ? new Date().toISOString() : null,

        status: completed ? "completed" : "new",
      },

      previousData: task,
    });
  };

  useEffect(() => {
    /*
     * We do not want to invalidate
     * the query when a task is
     * checked or unchecked.
     */
    if (
      isUpdatePending ||
      !isSuccess ||
      variables?.data?.done_date != undefined
    ) {
      return;
    }

    queryClient.invalidateQueries({
      queryKey: ["tasks", "getList"],
    });
  }, [queryClient, isUpdatePending, isSuccess, variables]);

  const labelId = `checkbox-list-label-${task.id}`;

  return (
    <>
      <div
        data-motion-item
        className="group flex items-start justify-between gap-2 rounded-lg px-3 py-2 -mx-3 cursor-pointer transition-colors duration-150 hover:bg-muted/60"
        onClick={() => setOpenWork(true)}
      >
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <div className="pt-1" onClick={(event) => event.stopPropagation()}>
            <Checkbox
              id={labelId}
              checked={!!task.done_date}
              onCheckedChange={handleCheck()}
              disabled={
                isUpdatePending ||
                (!canEditTask && String(task.sales_id) !== String(identity?.id))
              }
              className="mt-1"
            />
          </div>

          <div
            className={`flex-grow min-w-0 ${
              task.done_date ? "line-through" : ""
            }`}
          >
            <div className="mb-1">
              <TaskQuickStatus task={task} />
            </div>

            <div className="text-sm">
              {task.type && task.type !== "none" && (
                <>
                  <span className="font-semibold text-sm">
                    {(() => {
                      const matchedTaskType = taskTypes.find(
                        (taskType) => taskType.value === task.type,
                      );

                      return matchedTaskType
                        ? matchedTaskType.label
                        : task.type;
                    })()}
                  </span>
                  &nbsp;
                </>
              )}

              {task.text}
            </div>

            <div className="text-sm text-muted-foreground">
              Период: &nbsp;
              <span>{taskPeriodLabel(task, true)}</span>
              {showContact && firstContact && (
                <>
                  {" "}
                  {translate("resources.tasks.regarding_contact", {
                    name: getContactRepresentation(firstContact),
                  })}
                  {additionalContactsCount > 0 && (
                    <> +{additionalContactsCount}</>
                  )}
                </>
              )}
              {showAssignee && task.sales_id && (
                <ReferenceField<TData, Sale>
                  source="sales_id"
                  reference="sales"
                  record={task}
                  link={false}
                  className="inline text-sm text-muted-foreground"
                  render={({ referenceRecord }) => {
                    if (!referenceRecord) {
                      return null;
                    }

                    return (
                      <>
                        {" "}
                        · Ответственный:{" "}
                        {getSalesRepresentation(referenceRecord)}
                      </>
                    );
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {canEditTask && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 pr-0! size-8 cursor-pointer"
                aria-label={translate("resources.tasks.actions.title")}
                onClick={(event) => event.stopPropagation()}
              >
                <MoreVertical className="size-5 md:size-4" />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              align="end"
              onClick={(event) => {
                event.stopPropagation();
              }}
              onPointerDown={(event) => {
                event.stopPropagation();
              }}
            >
              <DropdownMenuItem
                className="cursor-pointer h-12 md:h-8 px-4 md:px-2 text-base md:text-sm"
                onClick={() => {
                  update("tasks", {
                    id: task.id,

                    data: {
                      due_date: postponeDeadline(task.due_date, 1),
                    },

                    previousData: task,
                  });
                }}
              >
                {translate("resources.tasks.actions.postpone_tomorrow")}
              </DropdownMenuItem>

              <DropdownMenuItem
                className="cursor-pointer h-12 md:h-8 px-4 md:px-2 text-base md:text-sm"
                onClick={() => {
                  update("tasks", {
                    id: task.id,

                    data: {
                      due_date: postponeDeadline(task.due_date, 7),
                    },

                    previousData: task,
                  });
                }}
              >
                {translate("resources.tasks.actions.postpone_next_week")}
              </DropdownMenuItem>

              <DropdownMenuItem
                className="cursor-pointer h-12 md:h-8 px-4 md:px-2 text-base md:text-sm"
                onClick={handleEdit}
              >
                {translate("ra.action.edit")}
              </DropdownMenuItem>

              <DropdownMenuItem
                className="cursor-pointer h-12 md:h-8 px-4 md:px-2 text-base md:text-sm"
                onClick={() => setDeleteOpen(true)}
              >
                {translate("ra.action.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <TaskDeleteAction
        task={task}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        hideTrigger
        onDeleted={() => setDeleteOpen(false)}
      />
      <TaskWorkDialog
        taskId={task.id}
        open={openWork}
        onOpenChange={setOpenWork}
      />

      {isMobile ? (
        <TaskEditSheet
          taskId={task.id}
          open={openEdit}
          onOpenChange={setOpenEdit}
        />
      ) : (
        <TaskEdit taskId={task.id} open={openEdit} close={handleCloseEdit} />
      )}
    </>
  );
};
